import { Typography, Chip } from '@mui/material';
import { useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DatabaseTable } from '../../../../shared/components/ui/DatabaseTable';
import { useAnnouncement } from '../../context/useAnnouncement';
import { ANNOUNCEMENT_TYPES } from '../../constants/announcementConstants';
import type { Announcement } from '../../types/announcementTypes';

interface AnnouncementTableProps {
  announcements: Announcement[];
  isLoading: boolean;
}

export const AnnouncementTable: React.FC<AnnouncementTableProps> = ({ 
  announcements, 
  isLoading 
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { openDeleteModal, navigateToEdit } = useAnnouncement();

  const getTypeLabel = (type: string) => {
    return ANNOUNCEMENT_TYPES.find(t => t.value === type)?.label || type;
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'ALL': return 'primary';
      case 'PER_APARTMENT': return 'secondary';
      case 'PER_RESIDENT': return 'info';
      default: return 'default';
    }
  };

  const handleView = (announcement: Announcement) => {
    navigate(`/announcements/${announcement.id}`);
  };

  const handleEdit = (announcement: Announcement) => {
    navigateToEdit(announcement.id);
  };

  const handleDelete = (announcement: Announcement) => {
    openDeleteModal(announcement);
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'Código', 
      width: 120,
      renderCell: (params: any) => (
        <Typography 
          sx={{ 
            fontFamily: 'monospace', 
            fontSize: 12,
            backgroundColor: '#F3F4F6',
            color: '#111827',
            px: 1,
            py: 0.5,
            borderRadius: 1
          }}
        >
          {params.value.slice(0, 8)}...
        </Typography>
      )
    },
    { 
      field: 'title', 
      headerName: 'Título', 
      flex: 1,
      renderCell: (params: any) => (
        <div>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            {params.row.description.length > 50 
              ? `${params.row.description.substring(0, 50)}...` 
              : params.row.description}
          </Typography>
        </div>
      )
    },
    { 
      field: 'type', 
      headerName: 'Tipo', 
      width: 150,
      renderCell: (params: any) => (
        <Chip 
          label={getTypeLabel(params.value)} 
          color={getTypeColor(params.value) as any}
          size="small"
        />
      )
    },
    { 
      field: 'shoot', 
      headerName: 'Enviado', 
      width: 120,
      renderCell: (params: any) => (
        <Chip 
          label={params.value ? 'Sim' : 'Não'} 
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    { 
      field: 'tenant', 
      headerName: 'Tenant', 
      flex: 1,
      renderCell: (params: any) => (
        <Typography variant="body2" fontSize={14} color={theme.palette.text.primary}>
          {params.value.fantasyName || params.value.name}
        </Typography>
      )
    },
  ];

  return (
    <DatabaseTable
      rows={announcements}
      columns={columns}
      loading={isLoading}
      onView={handleView}
     hideDelete
     hideEdit
    />
  );
};